import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { passwordResetApi } from '../hooks/usePasswordResetApi';
import { ApiClientError } from '../lib/api-client';
import logo from '../assets/images/logo.svg';

/**
 * ForgotPassword — page-level component for the `/forgot-password` route.
 *
 * Always shows a generic success message regardless of whether the email
 * matched an account (email-enumeration defence — mirrored at the auth
 * service in `PasswordResetService.requestReset`).
 *
 * @see HZVT-150_ADD_AUTH_PASSWORD_RESET_AND_EMAIL_VERIFICATION
 */
export const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await passwordResetApi.forgotPassword({ email });
      setSubmitted(true);
    } catch (err) {
      // We treat ALL errors here as "still show the success message" to
      // preserve the email-enumeration defence on the FE side too —
      // except for hard 400 (validation) which we surface so the user
      // knows the email is malformed.
      if (err instanceof ApiClientError && err.statusCode === 400) {
        setError('Please enter a valid email address.');
      } else {
        // For 5xx / network errors we still show the generic message
        // because surfacing them would let an attacker probe deliverability.
        setSubmitted(true);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8">
        <div className="flex justify-center mb-6">
          <img src={logo} alt="ZuroQ Logo" className="h-12 w-auto" />
        </div>
        <h1 className="text-2xl font-bold text-center text-gray-900 mb-2">Forgot Password</h1>
        <p className="text-center text-gray-600 text-sm mb-8">
          Enter your email and we&apos;ll send you a reset link if an account exists.
        </p>

        {submitted ? (
          <div
            data-testid="forgot-password-success"
            className="bg-green-50 border border-green-200 rounded-md p-4 text-center text-green-800"
          >
            <p className="font-medium">If an account exists for that email, we sent a link.</p>
            <p className="text-sm mt-1">Check your inbox (and spam folder) within a few minutes.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid w-full items-center gap-1.5">
              <Label htmlFor="email">Email address</Label>
              <Input
                type="email"
                id="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                required
              />
            </div>

            {error && (
              <div className="text-red-600 text-sm text-center" data-testid="forgot-password-error">
                {error}
              </div>
            )}

            <Button type="submit" className="w-full" disabled={submitting}>
              {submitting ? 'Sending...' : 'Send reset link'}
            </Button>
          </form>
        )}

        <p className="text-center text-sm text-gray-600 mt-6">
          Remembered it?{' '}
          <Link to="/login" className="font-medium text-indigo-600 hover:text-indigo-500">
            Back to login
          </Link>
        </p>
      </div>
    </div>
  );
};
